// src/geo/geo.js
import { reactive } from 'vue';

export const geo = {
  // Reactive location state (Vue will track mutations)
  location: reactive({
    latitude: null,
    longitude: null,
    useTempPosition: false,
    tempLatitude: null,
    tempLongitude: null,
  }),

  // Callbacks for location permission changes
  _permissionCallbacks: [],
  _deniedCallback: null,

  // Set callback for when location permission is denied
  onPermissionDenied(callback) {
    this._deniedCallback = callback;
  },

  // Watch for location permission changes
  watchLocationPermission(callback) {
    this._permissionCallbacks.push(callback);

    // Check if Permissions API is available
    if (navigator?.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((permissionStatus) => {
          // Call callback immediately with current state
          callback(permissionStatus.state === 'granted');

          // Listen for changes
          permissionStatus.addEventListener('change', () => {
            const isGranted = permissionStatus.state === 'granted';

            if (isGranted) {
              // Try to get location when permission is granted
              this.getLocation().catch(() => {});
            }

            // Notify all callbacks
            this._permissionCallbacks.forEach((cb) => cb(isGranted));
          });
        })
        .catch((err) => {
          console.error('Error watching location permission:', err);
        });
    } else {
      // If no Permissions API, we can't "watch" changes; at least provide best guess
      callback(false);
    }
  },

  // Remove a permission callback
  unwatchLocationPermission(callback) {
    const index = this._permissionCallbacks.indexOf(callback);
    if (index > -1) this._permissionCallbacks.splice(index, 1);
  },

  // Get current position - returns either actual or temporary position
  getCurrentPosition() {
    if (
      this.location.useTempPosition &&
      this.location.tempLatitude != null &&
      this.location.tempLongitude != null
    ) {
      return {
        latitude: this.location.tempLatitude,
        longitude: this.location.tempLongitude,
      };
    }
    return {
      latitude: this.location.latitude,
      longitude: this.location.longitude,
    };
  },

  // Set position from geolocation
  setPosition(position) {
    const newLat = position.coords.latitude;
    const newLon = position.coords.longitude;

    // Determine previous coords (prefer reactive state, fallback to storage)
    let prevLat = this.location.latitude;
    let prevLon = this.location.longitude;
    if ((prevLat == null || prevLon == null) && typeof localStorage !== 'undefined') {
      try {
        const stored = JSON.parse(localStorage.getItem('userLocation') || 'null');
        if (stored) {
          prevLat = prevLat ?? (stored.latitude ?? null);
          prevLon = prevLon ?? (stored.longitude ?? null);
        }
      } catch (e) {
        console.warn('[geo] failed to parse stored userLocation', e);
      }
    }

    // Haversine formula to compute meters between two lat/lon pairs
    const toRad = (v) => (v * Math.PI) / 180;
    const distanceMeters = (lat1, lon1, lat2, lon2) => {
      if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return Infinity;
      const R = 6371000; // meters
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    // Debounce threshold (meters)
    const DEBOUNCE_METERS = 10;
    const moved = distanceMeters(prevLat, prevLon, newLat, newLon);
    console.debug('[geo] setPosition called', { newLat, newLon, prevLat, prevLon, moved });

    // If we have previous coords and movement is smaller than threshold, skip update
    if (isFinite(moved) && moved < DEBOUNCE_METERS) {
      console.debug('[geo] movement below threshold — not updating reactive location');
      return this.getCurrentPosition();
    }

    // Significant move (or no previous coords): update reactive state and persist
    this.location.latitude = newLat;
    this.location.longitude = newLon;

    try {
      localStorage.setItem(
        'userLocation',
        JSON.stringify({ latitude: this.location.latitude, longitude: this.location.longitude })
      );
    } catch (e) {
      console.warn('[geo] failed to persist userLocation', e);
    }

    return this.getCurrentPosition();
  },

  // Set a temporary position manually
  setTempPosition(latitude, longitude) {
    this.location.tempLatitude = latitude;
    this.location.tempLongitude = longitude;
    this.location.useTempPosition = true;

    // Store temp position in localStorage
    localStorage.setItem(
      'tempLocation',
      JSON.stringify({
        latitude,
        longitude,
        useTempPosition: true,
      })
    );

    return this.getCurrentPosition();
  },

  // Stop using temporary position and revert to actual position
  clearTempPosition() {
    this.location.useTempPosition = false;
    localStorage.removeItem('tempLocation');
    return this.getCurrentPosition();
  },

  // Add this utility function near the top of your auth object:
  isGeolocationAvailable() {
    return typeof navigator !== 'undefined' && !!navigator.geolocation;
  },

  // Initialize from localStorage (safe to call on every load)
  _loadFromStorage() {
    const storedLocation = localStorage.getItem('userLocation');
    const storedTempLocation = localStorage.getItem('tempLocation');

    if (storedLocation) {
      try {
        const parsedLocation = JSON.parse(storedLocation);
        this.location.latitude = parsedLocation.latitude ?? null;
        this.location.longitude = parsedLocation.longitude ?? null;
      } catch (e) {
        console.warn('Failed to parse userLocation from localStorage', e);
      }
    }

    if (storedTempLocation) {
      try {
        const parsedTemp = JSON.parse(storedTempLocation);
        this.location.tempLatitude = parsedTemp.latitude ?? null;
        this.location.tempLongitude = parsedTemp.longitude ?? null;
        this.location.useTempPosition = !!parsedTemp.useTempPosition;
      } catch (e) {
        console.warn('Failed to parse tempLocation from localStorage', e);
      }
    }
  },

  // Get the user's geolocation (async: resolves when browser returns a fix)
  async getLocation(options = {}) {
    // First load any stored location so UI has something immediately
    this._loadFromStorage();

    const startTs = Date.now();
    console.debug('[geo] getLocation start', { options });
    console.debug('[geo] current reactive location (after load):', {
      latitude: this.location.latitude,
      longitude: this.location.longitude,
      useTempPosition: this.location.useTempPosition,
      tempLatitude: this.location.tempLatitude,
      tempLongitude: this.location.tempLongitude
    });

    // If temp is enabled, you may choose to skip refreshing actual location
    // (You can remove this if you'd still like to refresh in the background.)
    if (
      this.location.useTempPosition &&
      this.location.tempLatitude != null &&
      this.location.tempLongitude != null
    ) {
      return this.getCurrentPosition();
    }

    if (!this.isGeolocationAvailable()) {
      console.warn('Geolocation is not supported by this browser.');
      return this.getCurrentPosition();
    }

    const initialAttempt = options && options.initial === true;
    const defaultTimeout = initialAttempt ? (options.initialTimeoutMs ?? 3000) : 30000;

    const geoOpts = {
      enableHighAccuracy: true,
      timeout: defaultTimeout,
      maximumAge: 60_000, // allow cached location up to 60s old
      ...options,
    };

    return new Promise((resolve, reject) => {
      // Bound the wait ourselves even if the browser never invokes either callback.
      // A late successful fix still updates reactive state for location watchers.
      const fallbackTimer = setTimeout(() => {
        console.warn('[geo] location deadline reached — using cached/stored location');
        resolve(this.getCurrentPosition());
      }, geoOpts.timeout);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(fallbackTimer);
          try {
            const result = this.setPosition(position);
            console.debug('[geo] getLocation success', {
              coords: position.coords,
              elapsedMs: Date.now() - startTs,
              result
            });
            return resolve(result);
          } catch (e) {
            console.error('[geo] error in setPosition handler', e);
            return reject(e);
          }
        },
        (error) => {
          clearTimeout(fallbackTimer);
          console.error('[geo] getCurrentPosition error', {
            code: error?.code,
            message: error?.message,
            elapsedMs: Date.now() - startTs
          });

          // If the user explicitly denied permission, notify and reject so callers
          // (like NoLocation) can handle navigation.
          if (error && error.code === error.PERMISSION_DENIED) {
            console.warn('[geo] User denied the request for Geolocation.');
            if (this._deniedCallback) this._deniedCallback();
            return reject(error);
          }

          // For transient errors (timeout or unavailable), return whatever we
          // have from storage/reactive state instead of rejecting — this lets
          // callers continue and avoids redirect loops caused by temporary timeouts.
          if (error && (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE)) {
            console.warn('[geo] Geolocation timeout/unavailable — using cached/stored location');
            console.debug('[geo] returning stored position', this.getCurrentPosition());
            return resolve(this.getCurrentPosition());
          }

          // Fallback: resolve with current stored position rather than rejecting
          console.warn('[geo] Geolocation unknown error — returning stored position');
          console.debug('[geo] returning stored position (fallback)', this.getCurrentPosition());
          return resolve(this.getCurrentPosition());
        },
        geoOpts
      );
    });
  },

  // Polling control
  _pollIntervalId: null,
  _pollCount: 0,
  _lastPollTs: null,

  // Start polling geolocation at the given interval (ms). Returns true if started.
  startPolling(intervalMs = 30000, options = {}) {
    if (this._pollIntervalId) {
      console.debug('[geo] startPolling called but polling already active');
      return false;
    }

    console.debug('[geo] starting polling every', intervalMs, 'ms', { options });
    this._pollCount = 0;
    this._lastPollTs = null;

    // Immediately attempt a location fetch, then set interval
    (async () => {
      const startTs = Date.now();
      this._pollCount += 1;
      this._lastPollTs = startTs;
      console.debug('[geo] poll tick (immediate)', { count: this._pollCount, startTs });
      try {
        const res = await this.getLocation(options).catch(e => { throw e; });
        console.debug('[geo] poll tick success (immediate)', { count: this._pollCount, elapsedMs: Date.now() - startTs, result: res });
      } catch (e) {
        console.error('[geo] poll tick error (immediate)', { count: this._pollCount, elapsedMs: Date.now() - startTs, error: e });
      }
    })();

    this._pollIntervalId = setInterval(() => {
      const tickTs = Date.now();
      this._pollCount += 1;
      this._lastPollTs = tickTs;
      console.debug('[geo] poll tick', { count: this._pollCount, tickTs });
      this.getLocation(options)
        .then((res) => {
          console.debug('[geo] poll tick success', { count: this._pollCount, elapsedMs: Date.now() - tickTs, result: res });
        })
        .catch((err) => {
          console.error('[geo] poll tick error', { count: this._pollCount, elapsedMs: Date.now() - tickTs, error: err });
        });
    }, intervalMs);

    return true;
  },

  // Stop active polling
  stopPolling() {
    if (this._pollIntervalId) {
      clearInterval(this._pollIntervalId);
      this._pollIntervalId = null;
      console.debug('[geo] stopped polling', { pollCount: this._pollCount, lastPollTs: this._lastPollTs });
      this._pollCount = 0;
      this._lastPollTs = null;
      return true;
    }
    console.debug('[geo] stopPolling called but no active polling');
    return false;
  },

  // Return whether polling is active
  isPolling() {
    return !!this._pollIntervalId;
  },

  async hasAllowedLocation() {
    const hasCoords = this.location.latitude != null && this.location.longitude != null;

    // Guard: Permissions API may not exist (Safari can be quirky)
    if (!navigator?.permissions?.query) return hasCoords;

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state === 'granted' && hasCoords;
    } catch {
      return hasCoords;
    }
  },
};





// export const geo = {

//   // Location-related properties
//   location: {
//     latitude: null,
//     longitude: null,
//     useTempPosition: false,
//     tempLatitude: null,
//     tempLongitude: null
//   },

//   // Callbacks for location permission changes
//   _permissionCallbacks: [],
  
//   // Callback for when permission is denied
//   _deniedCallback: null,

//   // Set callback for when location permission is denied
//   onPermissionDenied(callback) {
//     this._deniedCallback = callback;
//   },

//   // Watch for location permission changes
//   watchLocationPermission(callback) {
//     this._permissionCallbacks.push(callback);
    
//     // Check if Permissions API is available
//     if (navigator.permissions && navigator.permissions.query) {
//       navigator.permissions.query({ name: 'geolocation' })
//         .then(permissionStatus => {
//           // Call callback immediately with current state
//           callback(permissionStatus.state === 'granted');
          
//           // Listen for changes
//           permissionStatus.addEventListener('change', () => {
//             const isGranted = permissionStatus.state === 'granted';
//             if (isGranted) {
//               // Try to get location when permission is granted
//               this.getLocation();
//             }
//             // Notify all callbacks
//             this._permissionCallbacks.forEach(cb => cb(isGranted));
//           });
//         })
//         .catch(err => {
//           console.error('Error watching location permission:', err);
//         });
//     }
//   },

//   // Remove a permission callback
//   unwatchLocationPermission(callback) {
//     const index = this._permissionCallbacks.indexOf(callback);
//     if (index > -1) {
//       this._permissionCallbacks.splice(index, 1);
//     }
//   },

//   // Get current position - returns either actual or temporary position
//   getCurrentPosition() {
//     if (this.location.useTempPosition && this.location.tempLatitude && this.location.tempLongitude) {
//       return {
//         latitude: this.location.tempLatitude,
//         longitude: this.location.tempLongitude
//       };
//     }
//     return {
//       latitude: this.location.latitude,
//       longitude: this.location.longitude
//     };
//   },

//   // Set position from geolocation
//   setPosition(position) {
//     this.location.latitude = position.coords.latitude;
//     this.location.longitude = position.coords.longitude;
//     // Store in localStorage for persistence
//     localStorage.setItem('userLocation', JSON.stringify({
//       latitude: this.location.latitude,
//       longitude: this.location.longitude
//     }));
//     return this.getCurrentPosition();
//   },

//   // Set a temporary position manually
//   setTempPosition(latitude, longitude) {
//     this.location.tempLatitude = latitude;
//     this.location.tempLongitude = longitude;
//     this.location.useTempPosition = true;
//     // Store temp position in localStorage
//     localStorage.setItem('tempLocation', JSON.stringify({
//       latitude,
//       longitude,
//       useTempPosition: true
//     }));
//     return this.getCurrentPosition();
//   },

//   // Stop using temporary position and revert to actual position
//   clearTempPosition() {
//     this.location.useTempPosition = false;
//     localStorage.removeItem('tempLocation');
//     return this.getCurrentPosition();
//   },

//   // Get the user's geolocation
//   getLocation() {
//     // First check if we have stored location
//     const storedLocation = localStorage.getItem('userLocation');
//     const storedTempLocation = localStorage.getItem('tempLocation');
    
//     if (storedLocation) {
//       const parsedLocation = JSON.parse(storedLocation);
//       this.location.latitude = parsedLocation.latitude;
//       this.location.longitude = parsedLocation.longitude;
//     }
    
//     if (storedTempLocation) {
//       const parsedTemp = JSON.parse(storedTempLocation);
//       this.location.tempLatitude = parsedTemp.latitude;
//       this.location.tempLongitude = parsedTemp.longitude;
//       this.location.useTempPosition = parsedTemp.useTempPosition;
//     }

//     // Then try to get current location
//     if (this.isGeolocationAvailable()) {
//       navigator.geolocation.getCurrentPosition(
//         (position) => this.setPosition(position),
//         (error) => {
//           console.error('Geolocation error:', error);
//           switch (error.code) {
//             case error.PERMISSION_DENIED:
//               console.warn("User denied the request for Geolocation.");
//               // Call the denied callback if registered
//               if (this._deniedCallback) {
//                 this._deniedCallback();
//               }
//               break;
//             case error.POSITION_UNAVAILABLE:
//               console.warn("Location information is unavailable.");
//               break;
//             case error.TIMEOUT:
//               console.warn("The request to get user location timed out.");
//               break;
//             case error.UNKNOWN_ERROR:
//               console.warn("An unknown error occurred.");
//               break;
//           }
//         }
//       );
//     } else {
//       console.warn("Geolocation is not supported by this browser.");
//     }
    
//     return this.getCurrentPosition();
//   },

//   hasAllowedLocation() {
//     // Returns true if the user has granted geolocation permission (not temp)
//     //if (!navigator.permissions) return !!(this.location.latitude && this.location.longitude);
//     return navigator.permissions.query({ name: 'geolocation' })
//       .then(result => result.state === 'granted' && !!(this.location.latitude && this.location.longitude))
//       .catch(() => !!(this.location.latitude && this.location.longitude));
//   },

//   // Add this utility function near the top of your auth object:
//   isGeolocationAvailable() {
//     return typeof navigator !== 'undefined' && !!navigator.geolocation;
//   },

// }

